/*
 * DateRangeFilter — checkin-date range picker for the Early Retention Checklist.
 *
 * Built on react-date-range (already a project dependency, themed the same way
 * as twilio_agent/RecentCallsFilters). Unlike that one it does NOT cap at today
 * — checkin dates are due-dates and can be in the future — and its presets
 * cover both past and upcoming ranges. Emits YYYY-MM-DD strings via
 * onChange({ from, to }); onClear resets to "any date".
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FaRegCalendarAlt, FaChevronDown, FaTimes } from 'react-icons/fa';
import { DateRangePicker, createStaticRanges } from 'react-date-range';
import moment from 'moment';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const buildStaticRanges = () =>
  createStaticRanges([
    {
      label: 'Today',
      range: () => ({
        startDate: moment().startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    },
    {
      label: 'Last 7 Days',
      range: () => ({
        startDate: moment().subtract(6, 'days').startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    },
    {
      label: 'Last 30 Days',
      range: () => ({
        startDate: moment().subtract(29, 'days').startOf('day').toDate(),
        endDate: moment().endOf('day').toDate()
      })
    },
    {
      label: 'Next 7 Days',
      range: () => ({
        startDate: moment().startOf('day').toDate(),
        endDate: moment().add(7, 'days').endOf('day').toDate()
      })
    },
    {
      label: 'Next 30 Days',
      range: () => ({
        startDate: moment().startOf('day').toDate(),
        endDate: moment().add(30, 'days').endOf('day').toDate()
      })
    }
  ]);

function DateRangeFilter({ from, to, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const handleClick = (e) => {
      const inRoot = rootRef.current && rootRef.current.contains(e.target);
      const inPop = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inRoot && !inPop) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const range = [
    {
      startDate: from ? new Date(from) : new Date(),
      endDate: to ? new Date(to) : new Date(),
      key: 'selection'
    }
  ];

  const handleSelect = (item) => {
    const { startDate, endDate } = item.selection;
    onChange({
      from: moment(startDate).format('YYYY-MM-DD'),
      to: moment(endDate).format('YYYY-MM-DD')
    });
  };

  const label =
    from && to ? `${moment(from).format('MMM D')} → ${moment(to).format('MMM D')}` : 'Any date';

  return (
    <div ref={rootRef} style={{ position: 'relative' }} className="inline-flex items-center gap-2">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          borderRadius: 8,
          fontSize: 13,
          cursor: 'pointer',
          border: '1px solid var(--input-border)',
          background: 'var(--input-bg)',
          color: 'var(--text-primary)'
        }}
      >
        <FaRegCalendarAlt size={12} style={{ color: 'var(--text-muted)' }} />
        <span>{label}</span>
        <FaChevronDown size={11} style={{ color: 'var(--text-muted)' }} />
      </button>
      {from && to ? (
        <button
          type="button"
          aria-label="Clear date filter"
          onClick={onClear}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 24,
            width: 24,
            borderRadius: 9999,
            border: '1px solid var(--input-border)',
            background: 'var(--input-bg)',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <FaTimes size={11} style={{ color: 'var(--text-muted)' }} />
        </button>
      ) : null}
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="erc-rdr-scope"
              style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 10000 }}
            >
              <style>{`
                .erc-rdr-scope .rdrStaticRanges { background: var(--surface-card); }
                .erc-rdr-scope .rdrStaticRange {
                  background: var(--surface-card);
                  border-bottom-color: var(--border-default);
                }
                .erc-rdr-scope .rdrStaticRangeLabel { color: var(--text-primary); }
                .erc-rdr-scope .rdrStaticRange:hover .rdrStaticRangeLabel,
                .erc-rdr-scope .rdrStaticRange:focus .rdrStaticRangeLabel {
                  background: var(--surface-sunken);
                }
              `}</style>
              <div
                style={{
                  overflow: 'hidden',
                  borderRadius: 8,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-card)',
                  boxShadow: 'var(--shadow-modal)'
                }}
              >
                <DateRangePicker
                  ranges={range}
                  onChange={handleSelect}
                  moveRangeOnFirstSelection={false}
                  editableDateInputs
                  rangeColors={['#ee731b']}
                  showDateDisplay={false}
                  months={2}
                  direction="horizontal"
                  inputRanges={[]}
                  staticRanges={buildStaticRanges()}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

DateRangeFilter.propTypes = {
  from: PropTypes.string,
  to: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
};

DateRangeFilter.defaultProps = {
  from: '',
  to: ''
};

export default DateRangeFilter;
